export default function HeroIllustration({ className = "" }) {
  return (
    <svg
      viewBox="0 0 480 360"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="bgGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FEF3E2" />
          <stop offset="100%" stopColor="#F1EDE3" />
        </linearGradient>
        <linearGradient id="docGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#F5F0E6" />
        </linearGradient>
        <linearGradient id="accentGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#155E75" />
          <stop offset="100%" stopColor="#F59E0B" />
        </linearGradient>
        <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="4" />
        </filter>
      </defs>

      {/* Background */}
      <rect width="480" height="360" fill="url(#bgGrad)" rx="12" />

      {/* Decorative dot grid */}
      <g opacity="0.35">
        {Array.from({ length: 12 }).map((_, r) =>
          Array.from({ length: 16 }).map((_, c) => (
            <circle
              key={`${r}-${c}`}
              cx={30 + c * 28}
              cy={30 + r * 26}
              r="1"
              fill="#94A3B8"
            />
          ))
        )}
      </g>

      {/* Back document (raw resume) */}
      <g transform="translate(60, 50)" className="animate-float-slow" style={{ transformOrigin: "140px 160px" }}>
        <rect
          x="0"
          y="0"
          width="160"
          height="220"
          rx="6"
          fill="url(#docGrad)"
          stroke="#CBD5E1"
          strokeWidth="1"
        />
        {/* Header strip */}
        <rect x="16" y="20" width="80" height="8" rx="2" fill="#CBD5E1" />
        <rect x="16" y="34" width="60" height="6" rx="2" fill="#E2E8F0" />
        {/* Sensitive info struck through */}
        <g>
          <rect x="16" y="56" width="90" height="6" rx="2" fill="#FCA5A5" />
          <line
            x1="14"
            y1="59"
            x2="108"
            y2="59"
            stroke="#DC2626"
            strokeWidth="1.5"
          />
          <rect x="16" y="68" width="70" height="6" rx="2" fill="#FCA5A5" />
          <line
            x1="14"
            y1="71"
            x2="88"
            y2="71"
            stroke="#DC2626"
            strokeWidth="1.5"
          />
        </g>
        {/* Body lines */}
        {[88, 100, 112, 124, 136, 156, 168, 180, 192].map((y, i) => (
          <rect
            key={`back-line-${y}`}
            x="16"
            y={y}
            width={i % 2 === 0 ? 128 : 100}
            height="4"
            rx="1.5"
            fill="#E2E8F0"
          />
        ))}
      </g>

      {/* Center lens — TalentLens motif */}
      <g transform="translate(232, 165)">
        <circle cx="0" cy="0" r="36" fill="#FFFFFF" stroke="#155E75" strokeWidth="1.5" opacity="0.15">
          <animate attributeName="r" values="28;36;28" dur="2.2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.3;0;0.3" dur="2.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="0" cy="0" r="28" fill="#FFFFFF" stroke="#155E75" strokeWidth="1.6" />
        {/* aperture blades inside the lens */}
        <g stroke="#155E75" strokeWidth="0.9" opacity="0.5" fill="none" strokeLinecap="round">
          {[0, 60, 120, 180, 240, 300].map((deg) => (
            <line
              key={deg}
              x1="0" y1="0"
              x2={20 * Math.cos((deg * Math.PI) / 180)}
              y2={20 * Math.sin((deg * Math.PI) / 180)}
            />
          ))}
        </g>
        <circle cx="0" cy="0" r="14" fill="none" stroke="#155E75" strokeWidth="1.1" opacity="0.7" />
        {/* amber center dot */}
        <circle cx="0" cy="0" r="5" fill="#F59E0B" />
        <circle cx="-1.4" cy="-1.4" r="1.8" fill="#FFFFFF" opacity="0.85" />
      </g>

      {/* Front document (parsed / branded) */}
      <g transform="translate(280, 38)" className="animate-float" style={{ transformOrigin: "360px 158px" }}>
        {/* shadow */}
        <rect
          x="6"
          y="8"
          width="160"
          height="240"
          rx="6"
          fill="#0F172A"
          opacity="0.06"
        />
        <rect
          x="0"
          y="0"
          width="160"
          height="240"
          rx="6"
          fill="#FFFFFF"
          stroke="#155E75"
          strokeWidth="1.2"
        />
        {/* Logo placeholder top-right */}
        <rect
          x="118"
          y="14"
          width="32"
          height="20"
          rx="3"
          fill="url(#accentGrad)"
        />
        {/* Name */}
        <rect x="14" y="22" width="78" height="9" rx="2" fill="#0E3A40" />
        <rect x="14" y="36" width="50" height="5" rx="1.5" fill="#94A3B8" />
        {/* Divider */}
        <line x1="14" y1="52" x2="146" y2="52" stroke="#E7E2D5" strokeWidth="1" />
        {/* Section headers + content */}
        <rect x="14" y="62" width="36" height="6" rx="1.5" fill="#155E75" />
        {[74, 84, 94].map((y, i) => (
          <rect key={`front-sec1-${y}`} x="14" y={y} width={i === 2 ? 90 : 130} height="4" rx="1" fill="#CFC8B6" />
        ))}
        <rect x="14" y="112" width="50" height="6" rx="1.5" fill="#155E75" />
        {[124, 134, 144, 154].map((y, i) => (
          <rect key={`front-sec2-${y}`} x="14" y={y} width={i % 2 === 0 ? 130 : 100} height="4" rx="1" fill="#CFC8B6" />
        ))}
        <rect x="14" y="172" width="44" height="6" rx="1.5" fill="#F59E0B" />
        {[184, 194, 204].map((y, i) => (
          <rect key={`front-sec3-${y}`} x="14" y={y} width={i === 1 ? 80 : 120} height="4" rx="1" fill="#CFC8B6" />
        ))}
        {/* Bottom footer */}
        <line x1="14" y1="220" x2="146" y2="220" stroke="#E7E2D5" strokeWidth="1" />
        <rect x="48" y="226" width="64" height="4" rx="1" fill="#94A3B8" />
      </g>

      {/* Decorative dots */}
      <circle cx="430" cy="50" r="4" fill="#F59E0B" opacity="0.5" />
      <circle cx="420" cy="320" r="6" fill="#155E75" opacity="0.35" />
      <circle cx="40" cy="310" r="5" fill="#F59E0B" opacity="0.3" />
    </svg>
  );
}
